import React, { useEffect, useState } from 'react';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAsk } from '../AskContext';
import { useAuth } from '../AuthContext';

const RETENTION_OPTIONS = [
  { days: 7,   label: '7 days',   note: 'Cheapest. Bodies older than a week are pulled live from your mailbox each time.' },
  { days: 30,  label: '30 days',  note: 'Default. Recent quotes stay instant; older bodies pulled live.' },
  { days: 90,  label: '90 days',  note: 'Higher S3 cost — covers a full quarter of activity.' },
  { days: 180, label: '180 days', note: 'Two quarters cached. Use if your sales cycle commonly exceeds 90 days.' },
  { days: 365, label: '365 days', note: 'Highest S3 cost — a full year of unlinked emails cached.' },
];

export default function DataRetention() {
  const [settings, setSettings] = useState(null);
  const [days, setDays] = useState(30);
  const [saving, setSaving] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const toast = useToast();
  const ask = useAsk();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin' || user?.role === 'Manager';

  useEffect(() => {
    api.getTenantSettings()
      .then((s) => { setSettings(s); setDays(s.emailRetentionDays || 30); })
      .catch((e) => toast.push(e.message, { variant: 'error' }));
  }, []); // eslint-disable-line

  async function save() {
    setSaving(true);
    try {
      const r = await api.updateTenantSettings({ emailRetentionDays: days });
      setSettings((s) => ({ ...s, emailRetentionDays: r.tenant.emailRetentionDays }));
      toast.push('Retention updated');
    } catch (e) {
      toast.push(e.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function runBackfill() {
    if (!(await ask({
      title: 'Run the one-shot body backfill?',
      body: 'Linked email bodies are archived to S3 and unlinked bodies are dropped from Mongo. Linked emails are preserved for audit.',
      confirmLabel: 'Run backfill',
      danger: true,
    }))) return;
    setBackfillBusy(true);
    try {
      const r = await api.runEmailBodyBackfill();
      setBackfillResult(r);
      toast.push(`Backfill complete · ${r.archived} archived · ${r.dropped} dropped · ${r.errors} errors`);
    } catch (e) {
      toast.push(e.message || 'Backfill failed', { variant: 'error' });
    } finally {
      setBackfillBusy(false);
    }
  }

  if (!settings) return <Shell title="Data retention"><div className="empty">Loading…</div></Shell>;

  const opts = settings.emailStorage?.retentionOptions
    ? RETENTION_OPTIONS.filter((o) => settings.emailStorage.retentionOptions.includes(o.days))
    : RETENTION_OPTIONS;
  const s3 = settings.emailStorage?.s3Enabled;

  return (
    <Shell title="Data retention" subtitle="Control how long supplier email bodies are cached for instant load.">
      <div className="card" style={{ maxWidth: 720 }}>
        <h3 className="card-title">How email storage works here</h3>
        <ul style={{ paddingLeft: 20, color: 'var(--ink-700)', fontSize: 14, lineHeight: '20px' }}>
          <li><b>Subjects + metadata</b> are stored in our database and kept forever.</li>
          <li><b>Bodies are pulled live from your mailbox</b> (Gmail, Outlook, IMAP) when you click an email — using the message-id we recorded at ingest. Subject-only listings stay fast.</li>
          <li><b>Linked emails</b> (used in a decision) are <b>archived to S3 forever</b> regardless of this setting. Audit trail is always intact.</li>
          <li><b>Unlinked email bodies</b> may be cached in S3 after you click them, for instant re-open. The cache is purged after the retention window below.</li>
          <li>Higher retention = more S3 storage cost, but faster repeat opens. Lower = cheaper, slower repeat opens.</li>
        </ul>
        {!s3 && (
          <div className="banner review" style={{ marginTop: 12 }}>
            <span>⚠</span>
            <div>
              S3 isn't configured for this environment, so bodies are <b>not cached</b> — every open pulls live from your mailbox. Set <span className="mono">S3_BUCKET_NAME</span> and apply the lifecycle policy to enable caching.
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ maxWidth: 720, marginTop: 16 }}>
        <h3 className="card-title">Email body retention window</h3>
        <div style={{ fontSize: 13, color: 'var(--ink-500)', marginBottom: 12 }}>
          Inbox listings show the last <b>{settings.emailRetentionDays || 30} days</b> by default. Toggle "Show all" in the Mailbox to override.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {opts.map((o) => (
            <label key={o.days} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12, border: '1px solid ' + (days === o.days ? 'var(--primary)' : 'var(--border-1)'), borderRadius: 6, cursor: isAdmin ? 'pointer' : 'not-allowed', background: days === o.days ? 'var(--primary-tint)' : '#fff' }}>
              <input
                type="radio"
                name="retention"
                disabled={!isAdmin}
                checked={days === o.days}
                onChange={() => setDays(o.days)}
                style={{ marginTop: 3 }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>{o.label}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{o.note}</div>
              </div>
            </label>
          ))}
        </div>
        {!isAdmin && (
          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 12 }}>
            Only Admins or Managers can change retention.
          </div>
        )}
        {isAdmin && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn" onClick={save} disabled={saving || days === settings.emailRetentionDays}>
              {saving ? 'Saving…' : 'Save retention'}
            </button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="card" style={{ maxWidth: 720, marginTop: 16, borderLeft: '4px solid var(--warning)', borderColor: 'var(--warning)' }}>
          <h3 className="card-title">One-shot backfill</h3>
          <div style={{ fontSize: 13, color: 'var(--ink-500)', marginBottom: 12 }}>
            Migrate every existing email in Mongo: archive bodies of <b>linked</b> emails to S3, then null out body fields on all rows. Subjects and metadata stay intact. Safe to re-run.
          </div>
          <button className="btn btn-secondary" onClick={runBackfill} disabled={backfillBusy}>
            {backfillBusy ? 'Running…' : 'Run backfill now'}
          </button>
          {backfillResult && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              Last run: <span className="chip ready" style={{ fontSize: 11 }}>{backfillResult.archived} archived</span>{' '}
              <span className="chip muted" style={{ fontSize: 11 }}>{backfillResult.dropped} dropped</span>{' '}
              {backfillResult.errors > 0 && <span className="chip risk" style={{ fontSize: 11 }}>{backfillResult.errors} errors</span>}
              {!backfillResult.s3Enabled && (
                <span style={{ marginLeft: 8, color: 'var(--ink-500)', fontSize: 12 }}>(S3 disabled — linked bodies kept in Mongo)</span>
              )}
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}
